import { TheWestSection } from '@news-mono/common'
import {
    BreachEvent,
    BreachScreenContext,
    BreachScreenType,
    click_origin_breach_screen,
    DataLayerEventName,
    SwgMetaInfo,
    useFeature,
    useSubscribeWithGoogle,
} from '@news-mono/web-common'
import { TheWestRegionalSections } from '@west-australian-newspapers/publication-types'
import React, {
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react'
import { getCartPayload } from '../../../..'
import { useAuthActions } from '../../../authentication/AuthenticationProvider'
import { useSection, useSectionMetaInfo } from '../../../../__helpers'
import { createLinkClickEvent } from '../../createLinkClickHandler'
import {
    BreachOptions,
    PackageOptions,
    StyledCancellationMessage,
    StyledSubscribeButtonsContainer,
    StyledSubscriptionPackageHeading,
    StyledSubscribeButton,
    StyledSwgButton,
    StyledPackageImage,
    StyledSubscriptionPackageContainer,
    StyledRecommendedBanner,
    StyledSubscriptionPackageHeadingContainer,
    StyledSubscriptionPackage,
    StyledBenefitListItemContainer,
    StyledBenefitListContainer,
    StyledImageAndHeaderContainer,
} from './SubscriptionPackage2026.styled'
import {
    BreachWallContentType,
    BreachWallStyle,
    SubscriptionPackageOptions,
    SubscriptionPackageTheme,
    SubscriptionPriceObject,
} from '../../helpers/BreachWallTypes'
import { ComputedPackageImage } from './ComputedPackageImage'
import {
    RadioSubscriptionPriceDisplay,
    SingleSubscriptionPriceDisplay,
} from './SubscriptionPriceDisplay'
import { StyledSubscriptionRadioRibbon } from './SubscriptionPriceDisplay.styled'
import {
    colors,
    IconTickOutline,
    isTabletOrMobileViewport,
} from '@news-mono/component-library'

interface SubscriptionPackageProps {
    breachCreativeName: string
    breachDeal: string
    breachStyle: BreachWallStyle
    breachContent?: BreachWallContentType
    packageInfo: SubscriptionPackageOptions
    packagePrices?: SubscriptionPriceObject[]
    selectedRadioPackage?: number
    masthead?: string
    packageTheme?: SubscriptionPackageTheme
    cancellationMessage?: string
    googleSKU?: string
    onEvent: (event: BreachEvent) => void
    isRegionalOverride: boolean
    isSwgPage?: boolean
    desiredRedirect?: string
}

const DEFAULT_BENEFIT_NAMES = [
    'Access to all digital content',
    'Regional online news',
    'The West app',
    'Daily digital edition newspaper',
    'Subscriber rewards',
    'Weekend papers delivered',
] as const

export const SubscriptionPackage2026: React.FC<SubscriptionPackageProps> = ({
    breachDeal,
    breachCreativeName,
    breachStyle,
    onEvent,
    masthead = 'The West Australian',
    packageInfo,
    packagePrices: providedPackagePrices,
    selectedRadioPackage: providedSelectedRadioPackage,
    isRegionalOverride,
    desiredRedirect,
    isSwgPage = false,
}) => {
    const {
        heading,
        ribbonTitle,
        ribbonOptions,
        packageTheme = 'default',
        buttonStyle,
        benefitOverride,
        type: subscriptionPackageType,
        isRecommended,
    } = packageInfo

    const isRadio = packageInfo.type === 'radio-list-subscriptions'
    const packagePrices = useMemo(
        () =>
            providedPackagePrices
                ? providedPackagePrices
                : isRadio
                ? packageInfo.prices
                : [packageInfo],
        [isRadio, packageInfo, providedPackagePrices],
    )
    const defaultPackage = isRadio ? packageInfo.prices[0] : packageInfo
    const [selectedPrice, setSelectedPrice] = useState(defaultPackage)
    const [storedProvidedPrice, setStoredProvidedPrice] = useState<
        SubscriptionPriceObject | undefined
    >()
    const { cancellationMessage, googleSKU, packagePath, offerCode } =
        selectedPrice

    // If a selected radio package index is provided, use it to set the selected price
    React.useEffect(() => {
        if (providedSelectedRadioPackage !== undefined) {
            const newSelectedPrice = packagePrices[providedSelectedRadioPackage]

            if (
                !storedProvidedPrice ||
                (newSelectedPrice && storedProvidedPrice !== newSelectedPrice)
            ) {
                setSelectedPrice(newSelectedPrice)
                setStoredProvidedPrice(newSelectedPrice)
            }
        }
    }, [
        providedSelectedRadioPackage,
        selectedPrice,
        packagePrices,
        storedProvidedPrice,
    ])

    // ---- Hooks ----
    const section = useSection<TheWestSection>()
    const sectionMeta = useSectionMetaInfo()
    const { onSubscribeClick } = useAuthActions()
    const isMobile = isTabletOrMobileViewport()

    // ---- Breach config options ----
    const isDigital = selectedPrice.packageType === 'digital'
    const isRegional = section in TheWestRegionalSections
    const isGuernseyEnabled = useFeature(
        'afl-footy-lives-here-guernsey-earlybird-promo',
    )
    const showGuernseyBenefit = isGuernseyEnabled && isRecommended
    const breachName = section === 'genwest' ? 'default' : section

    // Builds the benefit list based on the provided benefits and any additional benefits from the selected price point
    // If no benefits are provided, defaults to DEFAULT_BENEFIT_NAMES
    const benefits = useMemo(() => {
        // Combined benefits uses customBenefits if provided, otherwise defaults to DEFAULT_BENEFIT_NAMES
        let combinedBenefits: string[] = benefitOverride ?? [
            ...DEFAULT_BENEFIT_NAMES,
        ]

        // Append any additional benefits from the individual price points
        packagePrices.forEach((pricePoint) => {
            if (pricePoint.additionalBenefits) {
                if (pricePoint === selectedPrice) {
                    combinedBenefits = [
                        ...combinedBenefits,
                        ...pricePoint.additionalBenefits,
                    ]
                }
            }
        })

        return combinedBenefits
    }, [benefitOverride, packagePrices, selectedPrice])

    const onPackageChange = useCallback(
        (pricePoint: SubscriptionPriceObject) => {
            const current = selectedPrice

            setSelectedPrice(pricePoint)

            onEvent({
                type: DataLayerEventName.billingPeriodChange,
                originator: 'SubscriptionPackage',
                payload: {
                    from: {
                        weeklyPrice: current.weeklyPrice,
                        rateCode: current.rateCode,
                        offerCode: current.offerCode,
                    },
                    to: {
                        weeklyPrice: pricePoint.weeklyPrice,
                        rateCode: pricePoint.rateCode,
                        offerCode: pricePoint.offerCode,
                    },
                },
            })
        },
        // onEvent can cause excessive re-renders if included in deps
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedPrice],
    )

    const breachScreenContext = useContext(BreachScreenContext)
    const breachContext = breachScreenContext.context
    const breachScreenType: BreachScreenType = 'Premium - Inline'

    // ---- Subscribe with Google ----
    let swgMetaInfo: SwgMetaInfo | undefined = undefined
    const buttonRef = useRef<HTMLButtonElement>(null)
    // Matches swg-breach-albany-advertiser etc
    const isSWGBreachInterfaceEnabled = useFeature(`swg-breach-${breachName}`)
    const googleRrmeEnabled = useFeature('google-rrme-full-override')
    const subscribeWithGoogleEnabled = useFeature('subscribe-with-google')
    const showSwG =
        (subscribeWithGoogleEnabled || googleRrmeEnabled) &&
        isSWGBreachInterfaceEnabled &&
        googleSKU
    if (desiredRedirect) {
        swgMetaInfo = { desiredRedirect }
    }
    useSubscribeWithGoogle(
        buttonRef.current as HTMLButtonElement,
        googleSKU ?? 'everyday_digital_4weeks',
        sectionMeta,
        breachContext,
        swgMetaInfo,
        googleRrmeEnabled,
    )

    const addToCartPayload = getCartPayload(
        { price: selectedPrice, package: packageInfo },
        masthead,
        { quantity: selectedPrice.quantity },
    )

    const packageOptions: PackageOptions = {
        theme: packageTheme,
        type: selectedPrice.packageType,
        gridColumnPosition: packageInfo.columnPosition,
        isRecommended: showGuernseyBenefit,
    }
    const breachOptions: BreachOptions = {
        style: breachStyle,
        useBreachDealStyle: isRegionalOverride,
        isRegional: isRegional,
    }

    const packageImage = useMemo(() => {
        const BASE_IMAGE_PATH = './images/footy-lives-here-2026'
        let imageUrl = ''
        if (isDigital) {
            imageUrl = showGuernseyBenefit
                ? require(`${BASE_IMAGE_PATH}/afl_everyday_digital_with_guernsey.png`)
                : require(`${BASE_IMAGE_PATH}/afl_everyday_digital.png`)
        } else {
            imageUrl = showGuernseyBenefit
                ? require(`${BASE_IMAGE_PATH}/everyday_digital_papers_with_guernsey.png`)
                : require(`${BASE_IMAGE_PATH}/afl_everyday_digital_and_weekend_papers.png`)
        }
        return imageUrl
    }, [isDigital, showGuernseyBenefit])

    return (
        <StyledSubscriptionPackageContainer
            packageOptions={packageOptions}
            breachOptions={breachOptions}
        >
            {/* Recommended Package Banner */}
            {showGuernseyBenefit && (
                <StyledRecommendedBanner>
                    {isMobile ? 'LIMITED TIME ONLY' : 'LIMITED TIME -\u00A0'}
                    {!isMobile && <em>SAVE 25%</em>}
                </StyledRecommendedBanner>
            )}
            <StyledSubscriptionPackage>
                <StyledImageAndHeaderContainer>
                    {/* Package Image */}
                    {isDigital ? (
                        <StyledPackageImage
                            src={packageImage}
                            alt="The West Australian digital subscription package image"
                            role="presentation"
                        />
                    ) : selectedPrice.packageType === 'digitalprint' ? (
                        <StyledPackageImage
                            src={packageImage}
                            alt="The West Australian digital & print subscription package image"
                            role="presentation"
                        />
                    ) : (
                        <ComputedPackageImage section={section} />
                    )}
                    {/* Package Heading */}
                    <StyledSubscriptionPackageHeadingContainer>
                        {/* BluePillLight is the only ribbon that shows above the heading currently */}
                        {ribbonOptions &&
                            ribbonTitle &&
                            ribbonOptions === 'bluePillLight' && (
                                <StyledSubscriptionRadioRibbon
                                    ribbonOptions={ribbonOptions}
                                >
                                    {ribbonTitle}
                                </StyledSubscriptionRadioRibbon>
                            )}
                        <StyledSubscriptionPackageHeading>
                            {heading}
                        </StyledSubscriptionPackageHeading>
                    </StyledSubscriptionPackageHeadingContainer>
                </StyledImageAndHeaderContainer>
                {/* Display price points */}
                {subscriptionPackageType === 'single-subscription' ? (
                    <SingleSubscriptionPriceDisplay
                        subscriptionPackage={packageInfo}
                        packageOptions={packageOptions}
                        breachOptions={breachOptions}
                    />
                ) : (
                    <RadioSubscriptionPriceDisplay
                        subscriptionPackage={packageInfo}
                        packageOptions={packageOptions}
                        breachOptions={breachOptions}
                        selectedPricePoint={selectedPrice}
                        packagePrices={packagePrices}
                        setSelectedPricePoint={onPackageChange}
                    />
                )}
                <StyledSubscribeButtonsContainer>
                    {!isSwgPage && (
                        <StyledSubscribeButton
                            packageOptions={packageOptions}
                            breachOptions={breachOptions}
                            buttonStyle={buttonStyle}
                            type="button"
                            title="Subscribe Button"
                            aria-label="Subscribe button"
                            onClick={() => {
                                createLinkClickEvent(
                                    breachCreativeName,
                                    breachDeal,
                                    onEvent,
                                    breachScreenType,
                                    1,
                                    addToCartPayload,
                                )()
                                onSubscribeClick({
                                    packagePath,
                                    offerCode,
                                    callToAction: click_origin_breach_screen,
                                    breachScreenType: breachScreenType,
                                })
                            }}
                        >
                            Subscribe
                        </StyledSubscribeButton>
                    )}
                    {isDigital && showSwG && (
                        <StyledSwgButton
                            ref={buttonRef}
                            type="button"
                            aria-label="Subscribe with google button"
                        />
                    )}
                </StyledSubscribeButtonsContainer>
                {/* Benefit Lists */}
                <StyledBenefitListContainer>
                    {showGuernseyBenefit && (
                        <StyledBenefitListItemContainer
                            color={colors.thewest.lightBlack}
                        >
                            <IconTickOutline
                                width={24}
                                height={24}
                                color={colors.thewest.westblue}
                            />
                            <p>
                                <strong>
                                    <em>FREE</em> WA AAMI AFL Origin Guernsey
                                    (RRP $130).
                                </strong>
                            </p>
                        </StyledBenefitListItemContainer>
                    )}
                    {benefits.map((benefit) => (
                        <StyledBenefitListItemContainer key={benefit}>
                            <IconTickOutline
                                width={24}
                                height={24}
                                color={colors.thewest.westblue}
                            />
                            <p>{benefit}</p>
                        </StyledBenefitListItemContainer>
                    ))}
                </StyledBenefitListContainer>
                {cancellationMessage && (
                    <StyledCancellationMessage>
                        {cancellationMessage}
                    </StyledCancellationMessage>
                )}
            </StyledSubscriptionPackage>
        </StyledSubscriptionPackageContainer>
    )
}

SubscriptionPackage2026.displayName = 'SubscriptionPackage2026'
